import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Form, Select, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';

import { getLandlordProperties } from '~actions/reconciliation/bookinglist';
import { getSymbolFromCurrency } from '~helpers/currency';
import { MUTATION_UPDATE_RECONCILIATION_RENT } from '../../queries';

import Modal from 'common/components/modal';

const useStyles = makeStyles(() => ({
  wrapper: {
    '& .ant-modal-header': {
      padding: '32px 32px 24px 32px',
    },
    '& .ant-modal-body': {
      padding: '0px 32px 8px',
    },

    '& .ant-form-item-label': {
      lineHeight: '22px',
      marginBottom: 8,
    },
  },
}));

const EditProductInformationModal = ({
  form,
  refetchData,
  orderInfo,
  visible = false,
  setVisible = () => {},
}) => {
  const { getFieldDecorator, validateFields, setFieldsValue } = form;

  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  const [landlordProperties, setLandlordProperties] = useState([]);
  const [currPropertyId, setCurrPropertyId] = useState(orderInfo?.order?.propertyId ?? null);

  const handleCancelModal = () => {
    setVisible(false);
  };

  const handleSearchProperties = slug => {
    if (slug) {
      dispatch(
        getLandlordProperties({ slug }, result => {
          if (result?.properties?.edges) {
            setLandlordProperties(result?.properties?.edges);
          }
        }),
      );
    }
  };

  const [updateReconciliationRent, { loading: updateRentLoading }] = useMutation(
    MUTATION_UPDATE_RECONCILIATION_RENT,
    {
      fetchPolicy: 'network-only',
      onCompleted: res => {
        if (res?.updateOrderRent?.order?.id) {
          refetchData();
          handleCancelModal();
        }
      },
    },
  );

  const unitTypeRooms = useMemo(() => {
    const property = landlordProperties?.find(item => item.node.id === currPropertyId);

    return property?.node?.unitTypes?.edges ?? [];
  }, [landlordProperties, currPropertyId]);

  const handlePropertySearch = val => {
    handleSearchProperties(val);
  };

  const handlePropertyChange = val => {
    setCurrPropertyId(val);
    setFieldsValue({
      unitTypeId: undefined,
    });
  };

  const handleConfirmClick = () => {
    validateFields((err, values) => {
      if (!err) {
        const { unitTypeId, listingPrice } = values;
        const params = {
          id: orderInfo?.order?.id,
          unitTypeId,
          listingPrice: String(listingPrice),
        };

        updateReconciliationRent({
          variables: {
            input: params,
          },
        });
      }
    });
  };

  useEffect(() => {
    handleSearchProperties(orderInfo?.landlord?.slug);
  }, []);

  return (
    <Modal
      width={446}
      visible={visible}
      title={t('cms.reconciliation.lead_generation.update.modal.product.title')}
      okText={t('cms.reconciliation.lead_generation.update.modal.ok_btn')}
      wrapClassName={classes.wrapper}
      onCancel={handleCancelModal}
      onOk={handleConfirmClick}
      okButtonProps={{
        loading: updateRentLoading,
      }}
      destroyOnClose={true}
    >
      <Form>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item label={t('cms.reconciliation.lead_generation.update.modal.property')}>
              {getFieldDecorator('propertyId', {
                initialValue: orderInfo?.order?.propertyName,
              })(
                <Select onSearch={handlePropertySearch} onChange={handlePropertyChange}>
                  <For of={landlordProperties} each="property">
                    <Select.Option value={property?.node?.id}>{property?.node?.name}</Select.Option>
                  </For>
                </Select>,
              )}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={t('cms.reconciliation.lead_generation.update.modal.room')}>
              {getFieldDecorator('unitTypeId', {
                initialValue: orderInfo?.order?.unitTypeId,
                rules: [
                  {
                    validator: (_, value, callback) => {
                      value ? callback() : callback('Please select a room.');
                    },
                  },
                ],
              })(
                <Select
                  placeholder={t(
                    'cms.reconciliation.lead_generation.update.modal.unit_type_room.placeholder',
                  )}
                >
                  <For of={unitTypeRooms} each="room">
                    <Select.Option value={room.node.id}>{room.node.name}</Select.Option>
                  </For>
                </Select>,
              )}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={t('cms.reconciliation.lead_generation.update.modal.listing_price')}>
              {getFieldDecorator('listingPrice', {
                initialValue: orderInfo?.order?.listingSalesPrice ?? 0,
                rules: [
                  {
                    validator: (_, value, callback) => {
                      value
                        ? callback()
                        : callback(
                            t(
                              'cms.reconciliation.lead_generation.update.modal.listing_price.error',
                            ),
                          );
                    },
                  },
                ],
              })(
                <InputNumber
                  formatter={value =>
                    value
                      ? `${getSymbolFromCurrency(orderInfo?.order?.currency)} ${value}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ',',
                        )
                      : undefined
                  }
                  parser={value => value.replace(/[^0-9.]/g, '')}
                  min={0.01}
                  precision={2}
                  placeholder={t(
                    'cms.reconciliation.lead_generation.update.modal.listing_price.error',
                  )}
                />,
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Form.create({
  name: 'edit-product-information-modal',
})(EditProductInformationModal);
